import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import type {
  Friend,
  PhotoListItem,
  PaginatedResponse,
  AddPlayersToPhotoRequest,
  AddPlayersToPhotoResponse,
} from '@omokage/shared';
import { apiClient } from '../lib/api';

// Query keys
export const photoKeys = {
  all: ['photos'] as const,
  lists: () => [...photoKeys.all, 'list'] as const,
  list: (filters: string) => [...photoKeys.lists(), { filters }] as const,
  details: () => [...photoKeys.all, 'detail'] as const,
  detail: (id: string) => [...photoKeys.details(), id] as const,
};

/**
 * Get all friends from photo list cache
 * Aggregates friends from all cached photo queries
 */
export function useAllFriends(): Friend[] {
  const queryClient = useQueryClient();

  return useMemo(() => {
    // Get all cached photo queries
    const photoQueries = queryClient.getQueriesData<PaginatedResponse<PhotoListItem>>({
      queryKey: photoKeys.lists(),
    });

    // Aggregate all friends and deduplicate by vrchatId
    const friendsMap = new Map<string, Friend>();

    photoQueries.forEach(([_, data]) => {
      if (!data?.data) return;

      data.data.forEach((photo) => {
        photo.friends?.forEach((friend) => {
          if (!friendsMap.has(friend.vrchatId)) {
            friendsMap.set(friend.vrchatId, friend);
          }
        });
      });
    });

    // Return sorted by displayName
    return Array.from(friendsMap.values()).sort((a, b) =>
      a.displayName.localeCompare(b.displayName)
    );
  }, [queryClient]);
}

/**
 * Add players to a photo
 */
export function useAddPlayersToPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      photoId,
      vrchatIds,
    }: {
      photoId: string;
      vrchatIds: string[];
    }): Promise<AddPlayersToPhotoResponse> => {
      const response = await apiClient.post<{ data: AddPlayersToPhotoResponse }>(
        `/photos/${photoId}/friends`,
        { vrchatIds } as AddPlayersToPhotoRequest
      );
      return response.data;
    },

    onSuccess: (_, { photoId }) => {
      // Invalidate photo detail
      queryClient.invalidateQueries({ queryKey: photoKeys.detail(photoId) });
      // Invalidate photo lists (friends array changed)
      queryClient.invalidateQueries({ queryKey: photoKeys.lists() });
    },
  });
}

/**
 * Remove a player from a photo
 */
export function useRemovePlayerFromPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ photoId, vrchatId }: { photoId: string; vrchatId: string }) => {
      await apiClient.delete(`/photos/${photoId}/friends/${encodeURIComponent(vrchatId)}`);
    },

    onSuccess: (_, { photoId }) => {
      // Invalidate photo detail
      queryClient.invalidateQueries({ queryKey: photoKeys.detail(photoId) });
      // Invalidate photo lists (friends array changed)
      queryClient.invalidateQueries({ queryKey: photoKeys.lists() });
    },
  });
}
